import { ArrowLeft, Code, ExternalLink, Layers } from 'lucide-react';
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { Link, useLoaderData } from 'react-router';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { getSpecBySlug } from '~/lib/db/queries/specs';
import {
  coverageCount,
  getImplementationsForSpec,
  inferDepth,
  itemCount,
  missingCoverage,
} from '~/lib/implementation';
import type { ImplementationDepth } from '~/lib/implementation/types';

export const meta: MetaFunction<typeof loader> = ({ loaderData }) => [
  {
    title: loaderData
      ? `Implementations: ${loaderData.spec.name} — Specly`
      : 'Implementations — Specly',
  },
  {
    name: 'description',
    content: loaderData
      ? `Reference implementations for ${loaderData.spec.name} across frameworks.`
      : undefined,
  },
];

export async function loader({ params }: LoaderFunctionArgs) {
  const spec = await getSpecBySlug(params.slug!);
  if (!spec) throw new Response('Not Found', { status: 404 });

  const impls = getImplementationsForSpec(spec.slug);
  const specFeatureSlugs = spec.specFeatures.map((sf) => sf.feature.slug);

  const summaries = impls.map((impl) => ({
    meta: impl.meta,
    declaredDepth: impl.meta.depth,
    actualDepth: inferDepth(impl),
    coverage: coverageCount(impl),
    missing: missingCoverage(impl, specFeatureSlugs),
    items: itemCount(impl),
  }));

  return { spec, specFeatureSlugs, summaries };
}

const DEPTH_COLOR: Record<ImplementationDepth, string> = {
  overview: 'bg-sky-100 text-sky-800 border-sky-200',
  structural: 'bg-violet-100 text-violet-800 border-violet-200',
  detailed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

export default function ImplementationsList() {
  const { spec, specFeatureSlugs, summaries } = useLoaderData<typeof loader>();

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Link
          to={`/specs/${spec.slug}`}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="size-4" />
          Back to spec
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <Layers className="size-5 text-indigo-500" />
              <h1 className="text-3xl font-bold">Implementations</h1>
            </div>
            <p className="text-muted-foreground">
              Reference implementations of{' '}
              <span className="font-medium text-foreground">{spec.name}</span>{' '}
              across different frameworks and depths.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            render={<Link to={`/specs/${spec.slug}`} />}
          >
            View spec
          </Button>
        </div>
      </div>

      {summaries.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <Code className="size-8 mx-auto mb-3 text-muted-foreground opacity-60" />
          <p className="text-sm text-muted-foreground">
            No implementations registered for this spec yet.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Implementations are curated reference builds across different
            frameworks. Contributions coming soon.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {summaries.map((s) => (
            <Link
              key={s.meta.slug}
              to={`/specs/${spec.slug}/implementations/${s.meta.slug}`}
              className="block rounded-xl border border-border bg-card hover:border-primary/40 hover:bg-primary/5 transition-colors p-5"
            >
              <div className="flex items-start justify-between gap-4 mb-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h2 className="text-lg font-semibold">{s.meta.name}</h2>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full border font-medium ${DEPTH_COLOR[s.actualDepth]}`}
                      title={
                        s.actualDepth === s.declaredDepth
                          ? `Depth: ${s.actualDepth}`
                          : `Declared ${s.declaredDepth}, actual ${s.actualDepth}`
                      }
                    >
                      {s.actualDepth}
                    </span>
                    {s.meta.tags?.map((tag) => (
                      <Badge
                        key={tag}
                        variant="outline"
                        className="text-[10px]"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {s.meta.tagline}
                  </p>
                </div>
                {s.meta.sourceUrl && (
                  <a
                    href={s.meta.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex-shrink-0 text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                  >
                    Source <ExternalLink className="size-3" />
                  </a>
                )}
              </div>

              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-3">
                <span>
                  <span className="font-medium text-foreground">
                    {s.meta.framework}
                  </span>{' '}
                  · {s.meta.language}
                </span>
                {s.meta.stack.database && (
                  <span>DB: {s.meta.stack.database}</span>
                )}
                {s.meta.stack.auth && <span>Auth: {s.meta.stack.auth}</span>}
                {s.meta.stack.hosting && (
                  <span>Hosting: {s.meta.stack.hosting}</span>
                )}
              </div>

              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-2 pt-2 border-t border-border/60">
                <span>
                  Covers <b className="text-foreground">{s.coverage}</b> /{' '}
                  {specFeatureSlugs.length} features
                </span>
                <span>{s.items.tables} tables</span>
                <span>{s.items.routes} routes</span>
                <span>{s.items.modules} modules</span>
                {s.missing.length > 0 && (
                  <span className="text-amber-700">
                    Missing: {s.missing.slice(0, 3).join(', ')}
                    {s.missing.length > 3 ? '…' : ''}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
